/* eslint-disable react/prop-types */
import {useContext} from 'react'

import PropTypes from 'prop-types'

import {parseQueryString} from '@s-ui/js/lib/string'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount} from '@s-ui/react-hooks'
import EmptyCase, {emptyCaseDefaultSizes} from '@adv-mt/mt-empty-case-default'
import HeroDealersList, {HeroDealersListSelector} from '@adv-mt/mt-hero-dealers-list'
import Layout from '@adv-mt/mt-layout-basic'
import ListDealers from '@adv-mt/mt-list-dealers'
import ListMakes from '@adv-mt/mt-list-makes'
import ListPagination from '@adv-mt/mt-list-pagination'
import ListProvinces from '@adv-mt/mt-list-provinces'
import MotorTagCloud, {listTagcloudHeadingTags} from '@adv-mt/mt-list-tagcloud'
import SectionBasic, {sectionBasicMargins} from '@adv-mt/mt-section-basic'
import TitleBasic, {titleBasicColors, titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import TrackingPageView from '@adv-mt/mt-tracking-page-view'

import LinkFactory from '../../components/LinkFactory/index.js'
import {getInitialProps} from './getInitialProps.js'

const BASE_CLASS = 'mt-DealersList'
const RESULTS_CLASS = `${BASE_CLASS}-results`
const EMPTY_CLASS = `${BASE_CLASS}-empty`

const DealersList = ({
  city,
  h1,
  helmet = {},
  initialResultsResponse,
  make,
  makesList = [],
  pageName,
  paginationUrl,
  router,
  province,
  searchFilters,
  tagclouds = {},
  __HTTP__ = {}
}) => {
  // Even though this is placed at the top of the page,
  // When we do Client-side rendering the component is rendered
  const {redirectTo} = __HTTP__

  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const provinces = config.get('provinces')
  const featuredCities = config.get('FEATURED_CITIES')
  const {FEATURED_CITIES_DEALERS_COUNT} = config.get('dealersList')
  const {DEALERS_LIST_PATH} = config.get('page')
  const URL_STATICS_IMAGES = config.get('URL_STATICS_IMAGES')
  const {SELLER_TYPES_TRANSLATIONS} = config.get('tracking')
  const {SELLER_TYPE_PROFESSIONAL} = config.get('dealer')
  const dealerSellerType = SELLER_TYPES_TRANSLATIONS[SELLER_TYPE_PROFESSIONAL][config.get('DEFAULT_CULTURE')]

  const isProvinceSelected = Boolean(province)
  const isMakeSelected = Boolean(make)
  const isCitySelected = Boolean(city)

  const hasFiltersApplied = isProvinceSelected || isCitySelected || isMakeSelected

  const commonProvinces = provinces.map(province => ({
    ...province,
    url: `${DEALERS_LIST_PATH}${province.slug}/`
  }))

  const featuredProvinces = featuredCities.map(city => {
    const relatedProvince = commonProvinces.find(province => province.id === city.provinceId)
    const dealersCount = FEATURED_CITIES_DEALERS_COUNT.find(
      province => province.provinceId === city.provinceId
    )?.dealersCount

    return {
      id: relatedProvince.id,
      label: city.name,
      url: relatedProvince.url,
      slug: relatedProvince.slug,
      imageSrc: `${URL_STATICS_IMAGES}${city.imageSrc}`,
      dealersCount
    }
  })

  const handleRedirectTo = url => {
    router.push(url)
  }

  const handleOnProvinceSelected = ({provinceId}) => {
    const url = commonProvinces.find(({id}) => id === provinceId)?.url
    url && handleRedirectTo(url)
  }

  const titleBasicCommonProps = {
    size: titleBasicSizes.s,
    as: titleBasicTags.h2,
    color: titleBasicColors.gray
  }

  useMount(() => {
    const _searchUpdate$ = domain.get('get_search_url_dealer_use_case').subscribe(({result: [, {url}]}) => {
      const [pathname, queryString] = url.split('?')
      const query = parseQueryString(queryString)

      handleRedirectTo({
        pathname,
        query
      })
    })
    return () => _searchUpdate$.unsubscribe()
  })

  const {items, totalResults, totalPages, search} = initialResultsResponse || {}

  const {page: currentPage, sortBy} = search || {}

  const hasResults = Boolean(totalResults)

  const emptyCaseProps = {
    imageSize: emptyCaseDefaultSizes.MEDIUM,
    imageUrl: 'images/empty-cases/empty-case-search.svg',
    text: 'EMPTY_CASE_DEALERS_LIST_NO_RESULTS_TEXT',
    title: 'EMPTY_CASE_DEALERS_LIST_NO_RESULTS_TITLE'
  }

  const totalResultsText = `${totalResults} ${i18n.t('COMMON.DEALER', totalResults)}`

  if (redirectTo) {
    router.push(redirectTo)
    return <></>
  }

  const featuredMakesList = makesList.filter(make => make.isMostSearched)

  return (
    <Layout.Basic isMarginless linkFactory={LinkFactory}>
      <Helmet {...helmet} />

      <main className={BASE_CLASS}>
        {hasFiltersApplied ? (
          <>
            <SectionBasic margin={sectionBasicMargins.xxl}>
              <TitleBasic {...titleBasicCommonProps} as={titleBasicTags.h1} title={h1} />
            </SectionBasic>
            <SectionBasic margin={sectionBasicMargins.xxl}>
              <HeroDealersListSelector onProvinceSelected={handleOnProvinceSelected} />
            </SectionBasic>

            <SectionBasic margin={sectionBasicMargins.xxl}>
              <div className={RESULTS_CLASS}>
                <TitleBasic size={titleBasicSizes.xs} as={titleBasicTags.h2} title={totalResultsText} />
              </div>
              {hasResults ? (
                <>
                  <SectionBasic margin={sectionBasicMargins.xxl}>
                    <ListDealers
                      dealers={items}
                      isProvinceSelected={isProvinceSelected}
                      leadData={{seller_type: dealerSellerType}}
                    />
                    {/* TO DO: isProvinceSelected regarding hit_origin */}
                  </SectionBasic>
                  <ListPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    linkFactory={LinkFactory}
                    urlPattern={paginationUrl}
                  />
                </>
              ) : (
                <div className={EMPTY_CLASS}>
                  <EmptyCase {...emptyCaseProps} />
                </div>
              )}
            </SectionBasic>
          </>
        ) : (
          <>
            <SectionBasic margin={sectionBasicMargins.xxl}>
              <HeroDealersList onProvinceSelected={handleOnProvinceSelected} />
            </SectionBasic>
            <SectionBasic margin={sectionBasicMargins.l}>
              <TitleBasic {...titleBasicCommonProps} title={i18n.t('DEALERS_LIST.MAIN_PROVINCES_TITLE')} />
            </SectionBasic>
            <SectionBasic margin={sectionBasicMargins.xxl}>
              <ListProvinces provinces={featuredProvinces} linkFactory={LinkFactory} isFeatured />
            </SectionBasic>
            <SectionBasic>
              <TitleBasic {...titleBasicCommonProps} title={i18n.t('DEALERS_LIST.MAIN_MAKES_TITLE')} />
            </SectionBasic>
            <SectionBasic margin={sectionBasicMargins.xxl}>
              <ListMakes makes={featuredMakesList} linkFactory={LinkFactory} isFeatured />
            </SectionBasic>
          </>
        )}
        <SectionBasic>
          <MotorTagCloud
            initialTagclouds={tagclouds}
            pageName={pageName}
            linkFactory={LinkFactory}
            headingTag={listTagcloudHeadingTags.h2}
          />
        </SectionBasic>
      </main>
      <TrackingPageView
        pageName={pageName}
        province={province}
        make={make}
        search={searchFilters}
        searchResultsCount={totalResults}
        filters={{order: sortBy}}
        pageNumber={currentPage}
      />
    </Layout.Basic>
  )
}

DealersList.displayName = 'DealersList'

DealersList.propTypes = {
  city: PropTypes.string,
  h1: PropTypes.string,
  helmet: PropTypes.object,
  initialResultsResponse: PropTypes.shape({
    items: PropTypes.array,
    totalPages: PropTypes.number,
    totalResults: PropTypes.number,
    search: PropTypes.object
  }),
  make: PropTypes.string,
  makesList: PropTypes.array,
  pageName: PropTypes.string,
  pageErrors: PropTypes.shape({
    initialResultsError: PropTypes.object,
    paginationSearchUrlError: PropTypes.object
  }),
  searchFilters: PropTypes.object,
  paginationUrl: PropTypes.string,
  province: PropTypes.string,
  router: PropTypes.object,
  tagclouds: PropTypes.object
}

DealersList.getInitialProps = getInitialProps

export default DealersList
